/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component } from 'react';
import { ScrollView, View, StyleSheet } from 'react-native';

import Infotip from '../uiold/Infotip';
import { DataTable, SegmentedControl, Text } from '../uiold';

import NotesCell from './AttendanceTableNotesCell';

import stringCompare from '../../support/helpers/stringCompare';

import { VendorSummaryWithMarkets } from '../../types/shared/Vendor';
import { Status } from '../../types/shared/Attendance';
import { SortOrder } from '../uiold/DataTable/DataTableHeaderCell';
import { OptionsMap } from '../../support/helpers/CategoryOptions';

const StatusToLabel: Record<Status, string> = {
  Present: t('Present'),
  'No Show': t('No Show'),
  Late: t('Late'),
  Excused: t('Excused'),
};

export const ATTENDANCE_STATUSES: Array<Status> = Object.keys(StatusToLabel);

// Each `VendorAttendance` represent the attendance status of a vendor
// in a market date.
export type VendorAttendance = {
  vendor: VendorSummaryWithMarkets;
  status: Status | void;
  placementIndexes: Array<string>;
};

type SortableField = 'placement' | 'name' | 'contact' | 'category';

type Props = {
  attendance: Array<VendorAttendance>;
  onVendorStatusChange: (vendorID: string, status: Status) => void;
  onNotesPress: (vendor: VendorSummaryWithMarkets) => void;
  optionsMap: OptionsMap;
};

type State = {
  sortBy: SortableField;
  sortOrder: SortOrder;
};

type HeaderProps = {
  onHeaderPress: (field: SortableField) => void;
} & State;

type RowProps = VendorAttendance & {
  onVendorStatusChange: (vendorID: string, status: Status) => void;
  onNotesPress: (vendor: VendorSummaryWithMarkets) => void;
  optionsMap: OptionsMap;
};

export default class AttendanceTable extends Component<Props, State> {
  state: State = {
    sortBy: 'name',
    sortOrder: 'asc',
  };

  render() {
    let { attendance, ...otherProps } = this.props;
    let { sortOrder } = this.state;
    let sortedAttendance = this._sortAttendance(attendance);

    if (sortOrder === 'desc') {
      sortedAttendance.reverse();
    }

    return (
      <>
        <AttendanceTableHeader
          onHeaderPress={this._onHeaderPress}
          {...this.state}
        />
        <ScrollView>
          {sortedAttendance.map((attendee) => (
            <AttendanceTableRow
              key={attendee.vendor.id}
              {...attendee}
              {...otherProps}
            />
          ))}
        </ScrollView>
      </>
    );
  }

  _onHeaderPress = (field: SortableField) => {
    let { sortBy, sortOrder } = this.state;

    if (sortBy === field) {
      // Toggle the sort order
      this.setState({
        sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
      });
      return;
    }

    this.setState({
      sortBy: field,
    });
  };

  _sortAttendance = (
    attendance: Array<VendorAttendance>,
  ): Array<VendorAttendance> =>
    attendance.sort((a, b) => {
      switch (this.state.sortBy) {
        case 'category':
          return stringCompare(
            (a.vendor.category || [])[0] || '',
            (b.vendor.category || [])[0] || '',
          );
        case 'placement':
          return stringCompare(
            a.placementIndexes[0] || '',
            b.placementIndexes[0] || '',
          );
        default:
          return stringCompare(
            a.vendor[this.state.sortBy] || '',
            b.vendor[this.state.sortBy] || '',
          );
      }
    });
}

function AttendanceTableHeader({
  onHeaderPress,
  sortOrder,
  sortBy,
}: HeaderProps) {
  return (
    <View style={styles.headerRoot}>
      <DataTable.HeaderCell
        sortOrder={sortBy === 'placement' ? sortOrder : undefined}
        onPress={() => onHeaderPress('placement')}
        style={styles.placementIndexesColumn}
      >
        STALL
      </DataTable.HeaderCell>
      <DataTable.HeaderCell
        sortOrder={sortBy === 'name' ? sortOrder : undefined}
        onPress={() => onHeaderPress('name')}
      >
        NAME
      </DataTable.HeaderCell>
      <DataTable.HeaderCell
        sortOrder={sortBy === 'contact' ? sortOrder : undefined}
        onPress={() => onHeaderPress('contact')}
      >
        CONTACT PERSON
      </DataTable.HeaderCell>
      <DataTable.HeaderCell
        sortOrder={sortBy === 'category' ? sortOrder : undefined}
        onPress={() => onHeaderPress('category')}
      >
        CATEGORY
      </DataTable.HeaderCell>
      <View style={styles.statusColumn}>
        <Text type="label" numberOfLines={1} style={styles.headerText}>
          ATTENDANCE
        </Text>
      </View>
      <NotesHeaderCell />
    </View>
  );
}

class AttendanceTableRow extends Component<RowProps> {
  shouldComponentUpdate(prevProps: RowProps) {
    if (this.props.placementIndexes !== prevProps.placementIndexes) {
      return true;
    }
    if (this.props.status === prevProps.status) {
      return false;
    }

    return true;
  }

  render() {
    let { optionsMap, vendor, placementIndexes, status } = this.props;

    return (
      <DataTable.Row key={vendor.id}>
        <PlacementIndexesCell placementIndexes={placementIndexes} />
        <DataTable.Cell>{vendor.name}</DataTable.Cell>
        <DataTable.Cell>{vendor.contact}</DataTable.Cell>
        <DataTable.Cell>
          {(vendor.category || [])
            .map((id) => optionsMap.get(id)?.name ?? '')
            .join(', ')}
        </DataTable.Cell>
        <View style={styles.statusColumn}>
          <SegmentedControl // Vendor attendance status is null by default.
            activeSegment={status || ('' as any)}
            segments={Object.keys(StatusToLabel).map((status) => ({
              title: StatusToLabel[status],
              value: status,
            }))}
            onChange={this._onStatusChange}
          />
        </View>
        <NotesCell onPress={this._onNotesPress} />
      </DataTable.Row>
    );
  }

  _onStatusChange = (status: Status) => {
    let { vendor, onVendorStatusChange } = this.props;
    onVendorStatusChange(vendor.id, status);
  };

  _onNotesPress = () => {
    this.props.onNotesPress(this.props.vendor);
  };
}

function PlacementIndexesCell({
  placementIndexes,
}: {
  placementIndexes: Array<string>;
}) {
  let firstIndex = placementIndexes.shift();

  return (
    <View style={styles.placementIndexesColumn}>
      <Text type="label">{firstIndex}</Text>
      {placementIndexes.length ? (
        <View style={styles.infotip}>
          <Infotip text={placementIndexes.toString()} isShrinking oneLine />
        </View>
      ) : null}
    </View>
  );
}

function NotesHeaderCell() {
  return <View style={styles.notesHeader} />;
}

let styles = StyleSheet.create({
  headerRoot: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 5,
  },
  headerText: {
    fontWeight: '600',
  },
  notesHeader: {
    width: 48,
  },
  placementIndexesColumn: {
    width: 60,
    maxWidth: 60,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 40,
    zIndex: 2,
  },
  infotip: {
    position: 'absolute',
    left: 45,
  },
  statusColumn: {
    width: 400,
  },
});
